import React, { useState, useEffect } from "react";

import { courseList } from "../../api/Courses";

import { ToastMain, SweetAlertShow } from "../master/Master";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { useNavigate } from "react-router-dom";
import { getBatchList } from "../../api/BatchService";
import { getSessions } from "../../api/SessionsService";
import { StaffList } from "../../api/Staff";
import AcademicHeader from "../Academic/AcademicHeader";
import { AssignSyllabus, FetchSyllabus } from "../../api/SyllabusService";

export default function SyllabusAssignForm() {
  // =====================================================
  // STATES
  // =====================================================

  const [courses, setCourses] = useState([]);

  const [selectedCourse, setSelectedCourse] = useState("");

  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");

  const [existingTopics, setExistingTopics] = useState([]);

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [session_id, setSessionId] = useState("");

  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState([]);

  const [staffs, setStaffs] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState("");

  const [selectedSyllabusIds, setSelectedSyllabusIds] = useState([]);

  const [selectAll, setSelectAll] = useState(false);

  // Existing tracker combinations
  const [existingTrackers, setExistingTrackers] = useState([]);

  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);

  // =====================================================
  // GET ACTIVE SESSION
  // =====================================================

  const getSession = async () => {
    setLoading(true);

    try {
      const response = await getSessions();

      if (response?.data?.status) {
        const sessionData =
          response?.data?.data?.data || response?.data?.data || [];

        const list = Array.isArray(sessionData) ? sessionData : [];

        setSessions(list);

        // Active session default
        const activeSession = list.find(
          (session) => String(session?.status) === "1"
        );

        if (activeSession?.id) {
          setSessionId(String(activeSession.id));
        }
      } else {
        setSessions([]);
        setSessionId("");
      }
    } catch (error) {
      setSessions([]);

      ToastMain.fire({
        icon: "error",
        title: "Session fetch failed!",
      });
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // GET COURSE LIST
  // =====================================================

  const courseFetch = async () => {
    setLoading(true);

    try {
      const response = await courseList();

      if (response?.data?.status) {
        setCourses(response?.data?.data || []);
      } else {
        setCourses([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Course not found."
        );
      }
    } catch (error) {
      setCourses([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message ??
        "Something went wrong."
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // GET BATCH LIST
  // =====================================================

  const batchList = async () => {
    if (!session_id || !selectedCourse) {
      setBatches([]);
      return;
    }

    setLoading(true);

    try {
      const response = await getBatchList(
        `&status=1&session_id=${session_id}&course_id=${selectedCourse}`
      );

      if (response?.data?.status) {
        setBatches(response?.data?.data || []);
      } else {
        setBatches([]);
      }
    } catch (error) {
      setBatches([]);

      ToastMain.fire({
        icon: "error",
        title: "Something Went Wrong!",
      });
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // GET STAFF
  // =====================================================

  const getStaffs = async () => {
    setLoading(true);

    try {
      const response = await StaffList("&type=Teaching");

      if (response?.data?.status) {
        setStaffs(response?.data?.data || []);
      } else {
        setStaffs([]);
      }
    } catch (error) {
      setStaffs([]);

      ToastMain.fire({
        icon: "error",
        title: "Something Went Wrong!",
      });
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // INITIAL API CALLS
  // =====================================================

  useEffect(() => {
    getSession();
    courseFetch();
    getStaffs();
  }, []);

  // =====================================================
  // COURSE CHANGE
  // SUBJECTS FROM COURSE LIST
  // =====================================================

  const handleCourseChange = (e) => {
    const courseId = e.target.value;

    setSelectedCourse(courseId);

    // Reset dependent fields
    setSelectedSubject("");
    setSelectedBatch([]);
    setBatches([]);
    setExistingTopics([]);
    setSelectedSyllabusIds([]);
    setExistingTrackers([]);
    setSelectAll(false);

    if (!courseId) {
      setSubjects([]);
      return;
    }

    const selectedCourseData = courses.find(
      (course) => String(course?.id) === String(courseId)
    );

    if (!selectedCourseData) {
      setSubjects([]);
      return;
    }

    let courseSubjects = [];

    /*
      Course API ke andar subjects aa rahe hain.

      Example:

      {
        id: 51,
        title: "Class 10",
        subjects: [
          {
            id: 1,
            name: "Hindi"
          },
          {
            id: 2,
            name: "English"
          }
        ]
      }

      OR:

      subjects_list: [...]
    */

    if (Array.isArray(selectedCourseData?.subjects)) {
      courseSubjects = selectedCourseData.subjects;
    } else if (Array.isArray(selectedCourseData?.subjects_list)) {
      courseSubjects = selectedCourseData.subjects_list;
    }

    setSubjects(courseSubjects);
  };

  // =====================================================
  // BATCH FETCH
  // =====================================================

  useEffect(() => {
    if (session_id && selectedCourse) {
      batchList();
    } else {
      setBatches([]);
    }
  }, [session_id, selectedCourse]);

  // =====================================================
  // SUBJECT CHANGE
  // =====================================================

  const handleSubjectChange = (e) => {
    const subjectId = e.target.value;

    setSelectedSubject(subjectId);

    setExistingTopics([]);
    setSelectedSyllabusIds([]);
    setExistingTrackers([]);
    setSelectAll(false);
  };

  // =====================================================
  // BATCH CHANGE (multi-select checkboxes)
  // =====================================================

  const handleBatchToggle = (batchId) => {
    setSelectedBatch((previous) => {
      const updated = previous.includes(batchId)
        ? previous.filter((id) => id !== batchId)
        : [...previous, batchId];

      return updated;
    });

    setSelectedSyllabusIds([]);
    setSelectAll(false);
  };

  // =====================================================
  // FETCH SYLLABUS
  // =====================================================

  const syllabusFetch = async () => {
    if (!selectedCourse || !selectedSubject) {
      setExistingTopics([]);
      return;
    }

    setLoading(true);

    try {
      const response = await FetchSyllabus(
        `&course_id=${selectedCourse}&subject_id=${selectedSubject}&search=${search}`
      );

      if (
        response?.data?.status &&
        Array.isArray(response?.data?.data?.data)
      ) {
        const syllabusData = response.data.data.data;

        const existing = syllabusData.map((item) => ({
          id: item?.id,
          topic: item?.title || "",
          description: item?.description || "",
          duration: item?.duration_day || "",
        }));

        setExistingTopics(existing);
      } else {
        setExistingTopics([]);
      }

      setSelectedSyllabusIds([]);
      setSelectAll(false);
    } catch (error) {
      setExistingTopics([]);

      ToastMain.fire({
        icon: "error",
        title: "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // FETCH EXISTING TRACKERS
  // =====================================================

  const trackerFetch = async () => {
    if (
      !session_id ||
      !selectedCourse ||
      !selectedSubject ||
      selectedBatch.length === 0
    ) {
      setExistingTrackers([]);
      return;
    }

    try {
      const params = new URLSearchParams();

      params.append("session_id", session_id);
      params.append("course_id", selectedCourse);
      params.append("subject_id", selectedSubject);

      selectedBatch.forEach((batchId) => {
        params.append("batch_id[]", batchId);
      });

      const response = await AssignSyllabus(`?${params.toString()}`);

      if (
        response?.data?.status &&
        Array.isArray(response?.data?.data?.data)
      ) {
        setExistingTrackers(response.data.data.data);
      } else {
        setExistingTrackers([]);
      }
    } catch (error) {
      setExistingTrackers([]);
    }
  };

  // =====================================================
  // FETCH SYLLABUS WHEN SUBJECT CHANGES
  // =====================================================

  useEffect(() => {
    if (selectedCourse && selectedSubject) {
      syllabusFetch();
    } else {
      setExistingTopics([]);
    }
  }, [selectedCourse, selectedSubject]);

  // =====================================================
  // FETCH TRACKERS WHEN BATCH CHANGES
  // =====================================================

  useEffect(() => {
    if (
      session_id &&
      selectedCourse &&
      selectedSubject &&
      selectedBatch.length > 0
    ) {
      trackerFetch();
    } else {
      setExistingTrackers([]);
    }
  }, [session_id, selectedCourse, selectedSubject, selectedBatch]);

  // =====================================================
  // CHECK WHETHER SYLLABUS IS ALREADY ASSIGNED
  // =====================================================

  const isAlreadyAssigned = (syllabusId) => {
    if (!selectedBatch.length) {
      return false;
    }

    return selectedBatch.some((batchId) =>
      existingTrackers.some(
        (tracker) =>
          String(tracker?.batch_id) === String(batchId) &&
          String(tracker?.syllabus_id) === String(syllabusId)
      )
    );
  };

  // =====================================================
  // GET ASSIGNED BATCH NAMES FOR TOPIC
  // =====================================================

  const getAssignedBatchNames = (syllabusId) => {
    const names = [];

    selectedBatch.forEach((batchId) => {
      const exists = existingTrackers.some(
        (tracker) =>
          String(tracker?.batch_id) === String(batchId) &&
          String(tracker?.syllabus_id) === String(syllabusId)
      );

      if (exists) {
        const batch = batches.find(
          (item) => String(item?.id) === String(batchId)
        );

        if (batch) {
          names.push(batch?.title);
        }
      }
    });

    return names;
  };

  // =====================================================
  // CHECKBOX
  // =====================================================

  const handleCheckboxChange = (id) => {
    // Already assigned topic ko select nahi karna
    if (isAlreadyAssigned(id)) {
      return;
    }

    setSelectedSyllabusIds((previous) => {
      let updated;

      if (previous.includes(id)) {
        updated = previous.filter((item) => item !== id);
      } else {
        updated = [...previous, id];
      }

      const selectableTopics = existingTopics.filter(
        (item) => !isAlreadyAssigned(item?.id)
      );

      setSelectAll(
        updated.length === selectableTopics.length &&
        selectableTopics.length > 0
      );

      return updated;
    });
  };

  // =====================================================
  // SELECT ALL
  // =====================================================

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedSyllabusIds([]);
      setSelectAll(false);
      return;
    }

    const allIds = existingTopics
      .filter((item) => !isAlreadyAssigned(item?.id))
      .map((item) => item?.id)
      .filter(Boolean);

    setSelectedSyllabusIds(allIds);
    setSelectAll(allIds.length > 0);
  };

  // =====================================================
  // SUBMIT
  // =====================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCourse) {
      ToastMain.fire({ icon: "info", title: "Please select course!" });
      return;
    }

    if (!selectedBatch.length) {
      ToastMain.fire({ icon: "info", title: "Please select batch!" });
      return;
    }

    if (!selectedSubject) {
      ToastMain.fire({ icon: "info", title: "Please select subject!" });
      return;
    }

    if (!selectedStaff) {
      ToastMain.fire({ icon: "info", title: "Please select staff!" });
      return;
    }

    if (selectedSyllabusIds.length === 0) {
      ToastMain.fire({
        icon: "info",
        title: "Please select syllabus topic!",
      });
      return;
    }

    // =================================================
    // FRONTEND DUPLICATE CHECK
    // =================================================

    const duplicateTopics = selectedSyllabusIds.filter((syllabusId) =>
      isAlreadyAssigned(syllabusId)
    );

    if (duplicateTopics.length > 0) {
      ToastMain.fire({
        icon: "info",
        title: "Some selected topics are already assigned to selected batches.",
      });
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();

      formData.append("course_id", selectedCourse);
      formData.append("subject_id", selectedSubject);
      formData.append("teacher_id", selectedStaff);

      // Batch array
      selectedBatch.forEach((id) => {
        formData.append("batch_id[]", id);
      });

      // Syllabus array
      selectedSyllabusIds.forEach((id) => {
        formData.append("syllabus_id[]", id);
      });

      const response = await AssignSyllabus(formData);

      if (response?.data?.status) {
        const skipped = response?.data?.skipped || [];

        if (skipped.length > 0) {
          SweetAlertShow(
            "info",
            "Partially Assigned",
            `${response?.data?.message || "Syllabus assigned successfully."
            } ${skipped.length} batch/topic combination already assigned and skipped.`
          );
        } else {
          SweetAlertShow(
            "success",
            "Successfully",
            response?.data?.message || "Syllabus assigned successfully."
          );
        }

        navigate(-1);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Something went wrong."
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.message ??
        "Something went wrong."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSessionChange = (e) => {
    const sessionId = e.target.value;

    setSessionId(sessionId);

    setSelectedBatch([]);
    setBatches([]);

    setExistingTrackers([]);
    setSelectedSyllabusIds([]);
    setSelectAll(false);

    if (!sessionId || !selectedCourse) {
      return;
    }

    // useEffect automatically batch fetch karega
  };

  const selectedBatchNames = batches
    .filter((batch) => selectedBatch.includes(String(batch?.id)))
    .map((batch) => batch?.title);

  const selectableTopicCount = existingTopics.filter(
    (item) => !isAlreadyAssigned(item?.id)
  ).length;

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <>
      {/* =====================================================
          LOADING
      ===================================================== */}

      {loading && (
        <div
          className="d-flex justify-content-center align-items-center position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 9999, background: "rgba(255,255,255,0.6)" }}
        >
          <div className="sk-chase sk-primary display-1">
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
          </div>
        </div>
      )}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid mt-2">
                <div className="row">
                  <AcademicHeader />

                  <div className="col-md-12">
                    <div className="card shadow-sm border-0">
                      {/* =====================================================
                          HEADER
                      ===================================================== */}

                      <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-3 bg-label-primary d-flex align-items-center justify-content-center"
                            style={{ width: 44, height: 44 }}
                          >
                            <i className="ri-checkbox-multiple-line fs-4" />
                          </div>
                          <div>
                            <h4 className="m-0 text-primary">
                              Syllabus Assign
                            </h4>
                            <small className="text-muted">
                              Assign syllabus topics to a batch and teacher
                            </small>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => navigate(-1)}
                          disabled={loading}
                        >
                          <i className="ri-arrow-left-line me-1" />
                          Back
                        </button>
                      </div>

                      <div className="card-body">
                        <form onSubmit={handleSubmit}>
                          {/* =====================================================
                              COURSE / BATCH / SUBJECT / STAFF
                          ===================================================== */}

                          <div className="d-flex align-items-center gap-2 mb-2">
                            <span
                              className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: 24, height: 24, fontSize: 12 }}
                            >
                              1
                            </span>
                            <h6 className="mb-0">Assignment Details</h6>
                          </div>

                          <div className="row g-3 mb-2">
                            {/* SESSION */}
                            <div className="col-md-3">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={session_id}
                                  onChange={handleSessionChange}
                                  required
                                >
                                  <option value="">Select Session</option>
                                  {sessions?.map((session) => (
                                    <option
                                      key={session?.id}
                                      value={session?.id}
                                    >
                                      {session?.session}
                                      {String(session?.status) === "1"
                                        ? " (Active)"
                                        : ""}
                                    </option>
                                  ))}
                                </select>
                                <label>Session</label>
                              </div>
                            </div>

                            {/* COURSE */}
                            <div className="col-md-3">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={selectedCourse}
                                  onChange={handleCourseChange}
                                  required
                                >
                                  <option value="">Select Course</option>
                                  {courses?.map((course) => (
                                    <option key={course?.id} value={course?.id}>
                                      {course?.title}
                                    </option>
                                  ))}
                                </select>
                                <label>Course</label>
                              </div>
                            </div>

                            {/* SUBJECT */}
                            <div className="col-md-3">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={selectedSubject}
                                  onChange={handleSubjectChange}
                                  required
                                  disabled={!selectedCourse}
                                >
                                  <option value="">
                                    {!selectedCourse
                                      ? "Select Course First"
                                      : "Select Subject"}
                                  </option>
                                  {subjects?.map((subject) => (
                                    <option
                                      key={subject?.id}
                                      value={subject?.id}
                                    >
                                      {subject?.name}
                                    </option>
                                  ))}
                                </select>
                                <label>Subject</label>
                              </div>
                            </div>

                            {/* STAFF */}
                            <div className="col-md-3">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={selectedStaff}
                                  onChange={(e) =>
                                    setSelectedStaff(e.target.value)
                                  }
                                  required
                                >
                                  <option value="">Select Staff</option>
                                  {staffs?.map((staff) => (
                                    <option key={staff?.id} value={staff?.id}>
                                      {staff?.name}
                                    </option>
                                  ))}
                                </select>
                                <label>Staff</label>
                              </div>
                            </div>
                          </div>

                          {/* BATCH (multi-select as chips) */}
                          <div className="mb-4">
                            <label className="form-label small text-muted mb-2">
                              Batch{" "}
                              <span className="text-danger">*</span>{" "}
                              <span className="fst-italic">
                                (select one or more)
                              </span>
                            </label>

                            {!selectedCourse ? (
                              <div className="text-muted small">
                                Select a course to see available batches.
                              </div>
                            ) : batches.length === 0 ? (
                              <div className="text-muted small">
                                No active batch found for this course.
                              </div>
                            ) : (
                              <div className="d-flex flex-wrap gap-2">
                                {batches.map((batch) => {
                                  const isSelected = selectedBatch.includes(
                                    String(batch?.id)
                                  );

                                  return (
                                    <button
                                      key={batch?.id}
                                      type="button"
                                      className={`btn btn-sm ${isSelected
                                        ? "btn-primary"
                                        : "btn-outline-secondary"
                                        }`}
                                      onClick={() =>
                                        handleBatchToggle(String(batch?.id))
                                      }
                                    >
                                      {isSelected && (
                                        <i className="ri-check-line me-1" />
                                      )}
                                      {batch?.title}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* =====================================================
                              EXISTING TOPICS
                          ===================================================== */}

                          {selectedSubject && (
                            <>
                              <hr />

                              <div className="d-flex align-items-center gap-2 mb-3">
                                <span
                                  className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                  style={{
                                    width: 24,
                                    height: 24,
                                    fontSize: 12,
                                  }}
                                >
                                  2
                                </span>
                                <h6 className="mb-0">Select Topics</h6>
                              </div>
                            </>
                          )}

                          {/* =====================================================
                              NO SYLLABUS
                          ===================================================== */}

                          {existingTopics.length === 0 && selectedSubject && (
                            <div className="d-flex flex-column align-items-center justify-content-center text-center border rounded-3 py-4 mb-3 bg-light bg-opacity-50">
                              <i className="ri-file-list-3-line fs-2 text-muted mb-2" />
                              <span className="text-muted small">
                                No syllabus found for selected subject.
                              </span>
                            </div>
                          )}

                          {!selectedBatch.length && existingTopics.length > 0 && (
                            <div className="alert alert-light border d-flex align-items-center gap-2 mb-3">
                              <i className="ri-information-line text-primary" />
                              <span className="small text-muted">
                                Select at least one batch above to enable
                                topic selection.
                              </span>
                            </div>
                          )}

                          {existingTopics.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  checked={selectAll}
                                  onChange={handleSelectAll}
                                  id="selectAll"
                                  className="form-check-input"
                                  disabled={
                                    !selectedBatch.length ||
                                    selectableTopicCount === 0
                                  }
                                />
                                <label
                                  htmlFor="selectAll"
                                  className="form-check-label"
                                >
                                  Select all available ({selectableTopicCount})
                                </label>
                              </div>

                              {selectedSyllabusIds.length > 0 && (
                                <span className="badge bg-label-primary rounded-pill">
                                  {selectedSyllabusIds.length} selected
                                </span>
                              )}
                            </div>
                          )}

                          {/* =====================================================
                              TOPIC LIST
                          ===================================================== */}

                          {existingTopics.map((row, idx) => {
                            const alreadyAssigned = isAlreadyAssigned(row?.id);
                            const assignedBatchNames = getAssignedBatchNames(
                              row?.id
                            );
                            const isChecked = selectedSyllabusIds.includes(
                              row?.id
                            );

                            return (
                              <div
                                key={row?.id || idx}
                                className={`d-flex align-items-start gap-3 p-3 mb-2 rounded-3 border ${isChecked
                                  ? "border-primary bg-label-primary bg-opacity-10"
                                  : alreadyAssigned
                                    ? "bg-light bg-opacity-50"
                                    : ""
                                  }`}
                              >
                                <input
                                  type="checkbox"
                                  className="form-check-input mt-1 flex-shrink-0"
                                  style={{ width: 20, height: 20 }}
                                  checked={isChecked}
                                  disabled={
                                    alreadyAssigned || !selectedBatch.length
                                  }
                                  onChange={() =>
                                    handleCheckboxChange(row?.id)
                                  }
                                />

                                <div className="flex-grow-1">
                                  <div className="d-flex flex-wrap align-items-center gap-2">
                                    <span className="fw-semibold">
                                      {row?.topic || "Untitled topic"}
                                    </span>

                                    <span className="badge bg-label-info">
                                      <i className="ri-time-line me-1" />
                                      {row?.duration || 0} days
                                    </span>

                                    {alreadyAssigned ? (
                                      <span className="badge bg-danger">
                                        Already Assigned
                                      </span>
                                    ) : (
                                      <span className="badge bg-success">
                                        Available
                                      </span>
                                    )}
                                  </div>

                                  {row?.description && (
                                    <div className="text-muted small mt-1">
                                      {row.description}
                                    </div>
                                  )}

                                  {alreadyAssigned &&
                                    assignedBatchNames.length > 0 && (
                                      <div className="small text-muted mt-1">
                                        <i className="ri-team-line me-1" />
                                        Already assigned to:{" "}
                                        {assignedBatchNames.join(", ")}
                                      </div>
                                    )}
                                </div>
                              </div>
                            );
                          })}

                          {/* =====================================================
                              SUMMARY + BUTTONS
                          ===================================================== */}

                          <div className="d-flex flex-wrap justify-content-between align-items-center mt-4 pt-3 border-top">
                            <span className="text-muted small">
                              {selectedSyllabusIds.length > 0 ? (
                                <>
                                  Assigning{" "}
                                  <strong>
                                    {selectedSyllabusIds.length}
                                  </strong>{" "}
                                  topic
                                  {selectedSyllabusIds.length > 1 ? "s" : ""}{" "}
                                  to{" "}
                                  <strong>
                                    {selectedBatchNames.length || 0}
                                  </strong>{" "}
                                  batch
                                  {selectedBatchNames.length > 1 ? "es" : ""}
                                </>
                              ) : (
                                "Select topics above to assign"
                              )}
                            </span>

                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate(-1)}
                                disabled={loading}
                              >
                                Cancel
                              </button>

                              <button
                                type="submit"
                                className="btn btn-primary px-4"
                                disabled={
                                  loading || selectedSyllabusIds.length === 0
                                }
                              >
                                {loading ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                    />
                                    Assigning...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-checkbox-multiple-line me-1" />
                                    Assign Syllabus
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}